import {
  PDFArray,
  PDFDocument,
  PDFName,
  PDFRawStream,
  PDFRef,
  decodePDFRawStream,
} from "pdf-lib";
import {
  ZERO_LENGTH_STROKE,
  DOT_SEGMENT_LENGTH,
  MAX_COORDINATE,
  STREAM_ENCODING_KEYS,
  MAX_INPUT_BYTES,
  MAX_DECODED_BYTES,
  YIELD_AFTER_MS,
} from "../static/pdf.js";
import { bytesToString, stringToBytes } from "./pdf.js";

// Some report generators (Abbott rate charts) draw each data point as a zero-length
// stroked line with butt caps. Acrobat and the browser's native viewer paint a dot for
// it, but pdf.js draws it on a canvas, which paints nothing. Rewriting each one as a
// 0.01 pt open segment stroked with a round cap makes pdf.js draw the dot.

export const yieldToBrowser = () =>
  new Promise((resolve) => setTimeout(resolve, 0));
/**
 * Rewrites every zero-length stroked subpath of a content stream as
 * `q 1 J x y m x+0.01 y l <paint> Q`. The round cap lives inside its own q...Q, so
 * strokes around it keep the cap they had. One linear pass; q/Q nesting, unbalanced
 * q/Q and text strings do not matter.
 */
export const roundZeroLengthStrokeCaps = (content) =>
  content.replace(ZERO_LENGTH_STROKE, (whole, lead, x1, y1, x2, y2, paint) => {
    const x = parseFloat(x1);
    const y = parseFloat(y1);
    if (
      x !== parseFloat(x2) ||
      y !== parseFloat(y2) ||
      Math.abs(x) > MAX_COORDINATE ||
      Math.abs(y) > MAX_COORDINATE
    ) {
      return whole;
    }
    const xEnd = (x + DOT_SEGMENT_LENGTH).toFixed(5);
    return `${lead}q 1 J ${x1} ${y1} m ${xEnd} ${y1} l ${paint} Q`;
  });

// Only content streams can hold drawing operators: page contents, form XObjects (which
// include annotation appearances) and tiling patterns. Fonts, ICC profiles, embedded
// files and the like are never decoded.
const collectPageContentRefs = (pdfDoc) => {
  const refs = new Set();
  const visit = (entry) => {
    if (entry instanceof PDFArray) {
      entry.asArray().forEach(visit);
    } else if (entry instanceof PDFRef) {
      const target = pdfDoc.context.lookup(entry);
      if (target instanceof PDFArray) visit(target);
      else refs.add(entry.tag);
    }
  };
  pdfDoc
    .getPages()
    .forEach((page) => visit(page.node.get(PDFName.of("Contents"))));
  return refs;
};

const isContentStream = (ref, dict, pageContentRefs) =>
  pageContentRefs.has(ref.tag) ||
  dict.get(PDFName.of("Subtype")) === PDFName.of("Form") ||
  dict.has(PDFName.of("PatternType"));

/**
 * Returns PDF bytes whose zero-length stroked lines will show up in pdf.js.
 * The input bytes are returned untouched when nothing needs changing (or when the file
 * is too large to patch safely).
 */
export const fixZeroLengthStrokes = async (pdfBytes) => {
  if (pdfBytes.length > MAX_INPUT_BYTES) return pdfBytes;

  const pdfDoc = await PDFDocument.load(pdfBytes, { updateMetadata: false });
  const pageContentRefs = collectPageContentRefs(pdfDoc);
  let patched = 0;
  let decodedBytes = 0;
  let lastYield = Date.now();

  // eslint-disable-next-line no-restricted-syntax
  for (const [ref, object] of pdfDoc.context.enumerateIndirectObjects()) {
    if (
      object instanceof PDFRawStream &&
      isContentStream(ref, object.dict, pageContentRefs)
    ) {
      let original;
      try {
        original = bytesToString(decodePDFRawStream(object).decode());
      } catch {
        original = null;
      }

      if (original !== null) {
        decodedBytes += original.length;
        if (decodedBytes > MAX_DECODED_BYTES) return pdfBytes;

        const fixed = roundZeroLengthStrokeCaps(original);
        if (fixed !== original) {
          const replacement = pdfDoc.context.flateStream(stringToBytes(fixed));
          // Keep the original dictionary (form XObjects need Type, BBox, Resources...).
          object.dict.entries().forEach(([key, value]) => {
            if (!STREAM_ENCODING_KEYS.includes(key.asString())) {
              replacement.dict.set(key, value);
            }
          });
          pdfDoc.context.assign(ref, replacement);
          patched += 1;
        }
      }

      if (Date.now() - lastYield > YIELD_AFTER_MS) {
        // eslint-disable-next-line no-await-in-loop
        await yieldToBrowser();
        lastYield = Date.now();
      }
    }
  }

  return patched ? pdfDoc.save() : pdfBytes;
};
